"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Article = exports.echainIdDummy = void 0;
const echain_shared_i18n_1 = require("@igus/echain-shared-i18n");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const echain_shared_oo_1 = require("@igus/echain-shared-oo");
/**
 * The `Article` represents an igus echain article with all basic properties and methods.
 * It acts as base class for all articles by providing common attributes and methods.
 */
exports.echainIdDummy = '--EchainId--';
class Article {
    /**
     * An internal id representing an entitiy.
     * It is generated at construction using a json not having `EntityId` set or by the method
     * `duplicate`.
     * The method `fromJson` overwrites it.
     */
    get _entityId() {
        return this.model.EntityId;
    }
    set _entityId(value) {
        this.model.EntityId = value;
    }
    get entityId() {
        return this._entityId;
    }
    /**
     * The id used to identify an article at data layer. Invented articles do not have this property
     * set.
     */
    get id() {
        return this.model.Id;
    }
    set id(v) {
        this.model.Id = v;
    }
    /**
     * The echainId is used to identify an article. Invented articles do not have this property
     * set.
     */
    get echainId() {
        return this.model.EchainId !== exports.echainIdDummy ? this.model.EchainId : undefined;
    }
    /**
     *  stores the information which articleType the article is from
     */
    get articleType() {
        return this.model.ArticleType;
    }
    set articleType(v) {
        this.model.ArticleType = v;
    }
    /**
     *  Stores the information which articleSpecs the article has
     */
    get articleSpecs() {
        return this.model.ArticleSpecs;
    }
    set articleSpecs(v) {
        this.model.ArticleSpecs = v;
    }
    /**
     *  stores the information where the data is from
     */
    get dataSource() {
        return this.model.DataSource;
    }
    set dataSource(v) {
        this.model.DataSource = v;
    }
    /**
     * The article number used to identify an article throughout all igus systems.
     */
    get localizedArtNr() {
        if (!(0, echain_shared_i18n_1.isLocalizedText)(this.model.ArtNr)) {
            return undefined;
        }
        return this.model.ArtNr;
    }
    set localizedArtNr(v) {
        this.model.ArtNr = v;
    }
    /**
     * Get the translated artNr regarding the localeService
     */
    get artNr() {
        return echain_shared_articles_1.ArticleArtNrUtils.artNr(this.localizedArtNr);
    }
    /**
     * Set the translated artNr regarding the localeService
     */
    set artNr(value) {
        var _a, _b;
        if (value) {
            if (echain_shared_i18n_1.LocaleService.hasBeenInitialized()) {
                this.localizedArtNr = { [echain_shared_i18n_1.LocaleService.getArtNrCountry()]: value };
            }
            else {
                this.localizedArtNr = { DE: value };
            }
        }
        else if (this.localizedArtNr) {
            if (echain_shared_i18n_1.LocaleService.hasBeenInitialized()) {
                (_a = this.model.ArtNr) === null || _a === void 0 ? true : delete _a[echain_shared_i18n_1.LocaleService.getArtNrCountry()];
            }
            else {
                (_b = this.model.ArtNr) === null || _b === void 0 ? true : delete _b['DE'];
            }
        }
    }
    get artNrExists() {
        return this.model.ArtNrExists;
    }
    set artNrExists(value) {
        this.model.ArtNrExists = value;
    }
    get artNrGenerated() {
        return this.model.ArtNrGenerated;
    }
    set artNrGenerated(value) {
        this.model.ArtNrGenerated = value;
    }
    get bezeichnung() {
        return this.model.Bezeichnung;
    }
    set bezeichnung(value) {
        this.model.Bezeichnung = value;
    }
    /**
     * Get the translated bezeichnung regarding the localeService
     */
    get translatedBezeichnung() {
        return this.bezeichnung ? echain_shared_i18n_1.LocaleService.getUserArticleLocalizedText(this.bezeichnung) : undefined;
    }
    /**
     * Set the translated bezeichnung regarding the localeService
     */
    set translatedBezeichnung(value) {
        var _a;
        if (value) {
            this.bezeichnung = { [echain_shared_i18n_1.LocaleService.getArtNrCountry()]: value };
        }
        else if (this.bezeichnung) {
            (_a = this.model.Bezeichnung) === null || _a === void 0 ? true : delete _a[echain_shared_i18n_1.LocaleService.getArtNrCountry()];
        }
    }
    get widenBildId() {
        return this.model.WidenBildId;
    }
    set widenBildId(value) {
        this.model.WidenBildId = value;
    }
    get widenZeichnungId() {
        return this.model.WidenZeichnungId;
    }
    set widenZeichnungId(value) {
        this.model.WidenZeichnungId = value;
    }
    constructor(model) {
        this.model = model;
    }
    /**
     * Retrieves the artNr for quotes. This is by definition always the `artNrGenerated` if available.
     * If not, the raw `artNr` is retrieved, optionally by a given article number country.
     *
     * @param artNrCountry The article number country.
     * @returns The artNr to be used in quotes.
     */
    getQuoteArtNr(artNrCountry) {
        if (this.artNrGenerated)
            return this.artNrGenerated;
        if (artNrCountry && this.localizedArtNr && this.localizedArtNr[artNrCountry]) {
            return this.localizedArtNr[artNrCountry];
        }
        return this.artNr;
    }
    /**
     * Checks whether a given artNr is contained in the localizedArtNr object
     */
    hasArtNr(artNr) {
        if (this.localizedArtNr) {
            return Object.values(this.localizedArtNr).some((v) => v === artNr);
        }
        return false;
    }
    /**
     * Checks if a given article is the same entity, ignoring the reference. It is used to track
     * entities among clones.
     *
     * @param that Other article to check.
     * @returns The result.
     */
    is(that) {
        return this.entityId === that.entityId;
    }
    /**
     * Checks if a given article has the exact same values. It uses string comparison of the json
     * representation excluding the entityId.
     *
     * @param that Other article to check.
     * @param comparator An optional custom comparator function.
     * @returns The result.
     */
    hasEqualValues(that, comparator) {
        if (comparator)
            return comparator(this, that);
        const thisJson = Object.assign(Object.assign({}, this.toJson()), { EntityId: undefined });
        const thatJson = Object.assign(Object.assign({}, that.toJson()), { EntityId: undefined });
        return (0, echain_shared_ts_utils_1.deepEqual)(thisJson, thatJson);
    }
    /**
     * Checks if the article is referenced by an ArtNr string.
     * Since the reference could refer to an international or an US article number,
     * the following algorithm is used to check if the article is referenced.
     *
     * 1. Check the international article number against the given reference.
     * 2. Check the US article number against the given reference.
     * 3. OPTIONAL If checkArtNrGenerated is set to true then the artNrGenerated is checked against the given reference.
     *
     * @param referenceArtNr The given ArtNr reference string.
     * @param checkArtNrGenerated OPTIONAL Should the artNrGenerated be checked. Defaults to false.
     */
    isReferencedByArtNr(referenceArtNr, checkArtNrGenerated) {
        if (this.localizedArtNr) {
            return Boolean(this.localizedArtNr['DE'] === referenceArtNr ||
                this.localizedArtNr['US'] === referenceArtNr ||
                (checkArtNrGenerated && this.artNrGenerated === referenceArtNr));
        }
        return false;
    }
    /**
     * Checks if the article is referenced by an ArtNr start string.
     * Since the reference could refer to an international or an US article number,
     * the following algorithm is used to check if the article is referenced.
     *
     * 1. Check if the international article number starts with the given reference.
     * 2. Check if the US article number starts with the given reference.
     * 3. OPTIONAL If checkArtNrGenerated is set to true then it is checked if the artNrGenerated starts with the given reference.
     *
     * @param referenceArtNrStart The given ArtNr start reference string.
     * @param checkArtNrGenerated OPTIONAL Should the artNrGenerated be checked. Defaults to false.
     */
    isReferencedByArtNrStart(referenceArtNrStart, checkArtNrGenerated) {
        let result = false;
        if (this.localizedArtNr) {
            if (this.localizedArtNr['DE']) {
                result = result || this.localizedArtNr['DE'].startsWith(referenceArtNrStart);
            }
            if (this.localizedArtNr['US']) {
                result = result || this.localizedArtNr['US'].startsWith(referenceArtNrStart);
            }
        }
        if (checkArtNrGenerated && this.artNrGenerated) {
            result = result || this.artNrGenerated.startsWith(referenceArtNrStart);
        }
        return result;
    }
    /**
     * Clones a json into the model.
     *
     * It will remove any 'null' and 'undefined' values
     * to preserve the same behaviour as we had when using the Serialization framework.
     */
    fromJson(json, cloneStrategy = 'deep', deepCloneSanitizeOptions = { removeNully: true }, doNotOverwritePropertyKeys = []) {
        // remove properties that we want to preserve
        const jsonToUse = (0, echain_shared_ts_utils_1.getObjectEntries)(json).reduce((j, [k, v]) => {
            if (doNotOverwritePropertyKeys.includes(k))
                return j;
            j[k] = v;
            return j;
        }, {});
        switch (cloneStrategy) {
            case 'shallow':
                this.model = Object.assign(Object.assign({}, this.model), jsonToUse);
                return;
            case 'deep':
            default:
                this.model = Object.assign(Object.assign({}, this.model), (0, echain_shared_ts_utils_1.clonePrimitive)(jsonToUse, deepCloneSanitizeOptions));
                return;
            // todo outcomment this after removing Serialization
            // default:
            //   assertUnreachable(cloneStrategy);
        }
    }
    toJson(sanitizeOptions = { removeNully: true }) {
        return (0, echain_shared_ts_utils_1.clonePrimitive)(this.model, sanitizeOptions);
    }
    toModel() {
        return this.model;
    }
    _clone(baseArticle) {
        baseArticle.model = (0, echain_shared_ts_utils_1.clonePrimitive)(this.toModel());
        return baseArticle;
    }
    _duplicate() {
        const clone = this.clone();
        clone.model.EntityId = echain_shared_oo_1.UUIDGenerator.new();
        return clone;
    }
}
exports.Article = Article;
//# sourceMappingURL=Article.js.map